import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from "@angular/core";

import { Direction } from "../../../../shared/constants/enums";
import { LanguageService } from "../../../../shared/services/language-service";

const CENTER = 216;
const ARC_START = Math.PI - 0.5;
const ARC_SWEEP = Math.PI * 2 + 0.5 - ARC_START;
const TICK_INNER_RADIUS = 210;
const TICK_OUTER_RADIUS = 220;
const SCALE_RADIUS = 238;
const TICK_COUNT = 11;
const ARC_EASE = "stroke-dashoffset 3.5s cubic-bezier(0.16, 1, 0.3, 1)";
const ARC_SETTLE_MS = 3800;
const COUNT_DURATION_MS = 2200;
const COUNT_DELAY_MS = 300;

let nextGaugeId = 0;

const polar = (radius: number, angle: number) => ({
  x: CENTER + radius * Math.cos(angle),
  y: CENTER + radius * Math.sin(angle),
});

const tickAngle = (index: number) => ARC_START + (ARC_SWEEP / (TICK_COUNT - 1)) * index;

const clampPercent = (percent: number) => Math.min(Math.max(percent, 0), 100);

const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const playArc = (paths: SVGPathElement[], pill: SVGGElement, fill: number, reduced: boolean): (() => void) => {
  const [main] = paths;
  const measured = main.getTotalLength();
  const target = measured * (1 - fill);
  let frame = 0;

  const placePill = (visible: number) => {
    if (visible <= 0 || visible > measured) return;
    const point = main.getPointAtLength(visible);
    const previous = main.getPointAtLength(Math.max(visible - 2, 0));
    const angle = (Math.atan2(point.y - previous.y, point.x - previous.x) * 180) / Math.PI;
    pill.setAttribute("transform", `translate(${point.x},${point.y}) rotate(${angle})`);
    pill.style.opacity = "1";
  };

  paths.forEach((path) => {
    path.style.transition = "none";
    path.style.strokeDasharray = String(measured);
    path.style.strokeDashoffset = String(measured);
  });
  pill.style.opacity = "0";

  if (reduced) {
    paths.forEach((path) => (path.style.strokeDashoffset = String(target)));
    placePill(measured - target);
    return () => undefined;
  }

  main.getBoundingClientRect();
  paths.forEach((path) => (path.style.transition = ARC_EASE));

  frame = requestAnimationFrame(() => {
    paths.forEach((path) => (path.style.strokeDashoffset = String(target)));
    frame = requestAnimationFrame(function follow() {
      const offset = parseFloat(getComputedStyle(main).strokeDashoffset) || measured;
      placePill(measured - offset);
      frame = requestAnimationFrame(follow);
    });
  });

  const settleTimer = window.setTimeout(() => cancelAnimationFrame(frame), ARC_SETTLE_MS);

  return () => {
    cancelAnimationFrame(frame);
    clearTimeout(settleTimer);
  };
};

const playCount = (end: number, decimals: number, reduced: boolean, render: (text: string) => void): (() => void) => {
  if (reduced) {
    render(end.toFixed(decimals));
    return () => undefined;
  }

  let frame = 0;
  render((0).toFixed(decimals));

  const delayTimer = window.setTimeout(() => {
    const start = performance.now();
    frame = requestAnimationFrame(function tick(now) {
      const linear = Math.min((now - start) / COUNT_DURATION_MS, 1);
      const eased = 1 - Math.pow(1 - linear, 3);
      render((eased * end).toFixed(decimals));
      if (linear < 1) frame = requestAnimationFrame(tick);
    });
  }, COUNT_DELAY_MS);

  return () => {
    cancelAnimationFrame(frame);
    clearTimeout(delayTimer);
  };
};

@Component({
  selector: "adaa-adaa4-gauge",
  templateUrl: "./adaa4-gauge.html",
  styleUrl: "./adaa4-gauge.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Adaa4GaugeComponent {
  private readonly _languageService = inject(LanguageService);

  readonly value = input.required<number>();

  readonly prefix = `adaa4-gauge-${nextGaugeId++}`;
  readonly urls = {
    pillBlur: `url(#${this.prefix}-pill-blur)`,
    glowInner: `url(#${this.prefix}-glow-inner)`,
    trackGreen: `url(#${this.prefix}-track-green)`,
  };

  readonly displayed = signal("0");

  readonly isRtl = computed(() => {
    this._languageService.current();
    return this._languageService.direction() === Direction.RTL;
  });

  readonly ticks = Array.from({ length: TICK_COUNT }, (_, index) => {
    const inner = polar(TICK_INNER_RADIUS, tickAngle(index));
    const outer = polar(TICK_OUTER_RADIUS, tickAngle(index));
    return { x1: inner.x.toFixed(3), y1: inner.y.toFixed(3), x2: outer.x.toFixed(3), y2: outer.y.toFixed(3) };
  });

  readonly scale = computed(() =>
    Array.from({ length: TICK_COUNT }, (_, index) => {
      const point = polar(SCALE_RADIUS, tickAngle(index));
      const label = this.isRtl() ? 100 - index * 10 : index * 10;
      return { x: point.x.toFixed(3), y: point.y.toFixed(3), label };
    })
  );

  readonly arcsTransform = computed(() => (this.isRtl() ? `translate(${CENTER * 2},0) scale(-1,1)` : null));

  private readonly _greenValue = viewChild.required<ElementRef<SVGPathElement>>("greenValue");
  private readonly _greenGlow = viewChild.required<ElementRef<SVGPathElement>>("greenGlow");
  private readonly _greenPill = viewChild.required<ElementRef<SVGGElement>>("greenPill");

  constructor() {
    afterRenderEffect((onCleanup) => {
      const value = this.value();
      const reduced = prefersReducedMotion();

      const cancelGreen = playArc(
        [this._greenValue().nativeElement, this._greenGlow().nativeElement],
        this._greenPill().nativeElement,
        clampPercent(value) / 100,
        reduced
      );
      const cancelCount = playCount(value, value < 10 ? 1 : 0, reduced, (text) => this.displayed.set(text));

      onCleanup(() => {
        cancelGreen();
        cancelCount();
      });
    });
  }
}
